import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { CashReconciliationDetail } from '../../../../../Model/cashReconciliationXModel'
import { cashReconciliationBXService } from '../../../../../Services/cashReconciliationBXService'
import fillingStationsService from '../../../../../Services/fillingStationsService'
import { userBXService } from '../../../../../Services/userBXService'
import {
  formatMinorCurrency,
  majorToMinor,
  minorToMajorInput,
} from '../../../../../Utils/cashReconciliationMoney'
import PublishReconciliationModal from './PublishReconciliationModal'
import RejectReconciliationModal from './RejectReconciliationModal'
import './CashReconciliationDetail.scss'

type Mutation = 'deposit' | 'publish' | 'reject' | 'retry' | null

type RequestError = {
  response?: {
    data?: {
      error?: string
      message?: string
      details?: Record<string, { message?: string }>
      errors?: Array<{ errors?: string[] }>
    }
  }
}

const getRequestErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as RequestError).response?.data
  const fieldError = data?.errors?.flatMap((item) => item.errors ?? []).find(Boolean)
  const detailError = Object.values(data?.details ?? {})
    .map((item) => item.message)
    .find(Boolean)

  return fieldError || detailError || data?.error || data?.message || fallback
}

const formatDateTime = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat('en-NG', { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(value),
      )
    : 'Not available'

const portalUserNameCache = new Map<string, string>()

const CashReconciliationDetailPage = () => {
  const { submissionId } = useParams<{ submissionId: string }>()
  const navigate = useNavigate()
  const [submission, setSubmission] = useState<CashReconciliationDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [confirmedDepositInput, setConfirmedDepositInput] = useState('')
  const [overrideReasonInput, setOverrideReasonInput] = useState('')
  const [depositError, setDepositError] = useState('')
  const [isEditingDeposit, setIsEditingDeposit] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [actorNames, setActorNames] = useState<Record<string, string>>({})
  const [mutation, setMutation] = useState<Mutation>(null)

  const portalActorRefsKey = Array.from(
    new Set(
      (submission?.statusHistory ?? [])
        .filter((entry) => entry.actorRefKind === 'portalUser')
        .map((entry) => entry.actorRef),
    ),
  )
    .sort()
    .join('|')

  useEffect(() => {
    let cancelled = false

    const loadDetails = async (showLoader: boolean, loadStationName: boolean) => {
      if (!submissionId) {
        setLoadError('This report does not have a valid ID.')
        setLoading(false)
        return
      }

      try {
        if (showLoader) setLoading(true)
        setLoadError('')
        const response = await cashReconciliationBXService.getById(submissionId)
        if (cancelled) return

        const report = response.data.data
        setSubmission((current) => ({
          ...report,
          stationName: current?.stationName ?? report.stationName,
        }))
        if (showLoader) {
          setConfirmedDepositInput(minorToMajorInput(report.confirmedCashDepositMinor))
          setOverrideReasonInput(report.confirmedCashDepositReason ?? '')
        }

        if (loadStationName) try {
          const stationResponse = await fillingStationsService.getById(report.stationId)
          if (cancelled || !stationResponse.data) return

          const station = stationResponse.data
          const stationName =
            [station.businessName, station.branchName].filter(Boolean).join(': ') ||
            report.stationId
          setSubmission((current) => (current ? { ...current, stationName } : current))
        } catch {
          // The report remains usable with its station ID when station details are unavailable.
        }
      } catch (error) {
        if (!cancelled) {
          setSubmission(null)
          setLoadError(getRequestErrorMessage(error, 'Failed to load the station report.'))
        }
      } finally {
        if (!cancelled && showLoader) setLoading(false)
      }
    }

    loadDetails(true, true)
    const refreshOnFocus = () => loadDetails(false, false)
    window.addEventListener('focus', refreshOnFocus)
    return () => {
      cancelled = true
      window.removeEventListener('focus', refreshOnFocus)
    }
  }, [submissionId])

  useEffect(() => {
    if (!portalActorRefsKey) return

    let cancelled = false
    const actorRefs = portalActorRefsKey.split('|')
    const cachedNames = Object.fromEntries(
      actorRefs
        .filter((actorRef) => portalUserNameCache.has(actorRef))
        .map((actorRef) => [actorRef, portalUserNameCache.get(actorRef) as string]),
    )

    if (Object.keys(cachedNames).length) {
      setActorNames((current) => ({ ...current, ...cachedNames }))
    }

    const unresolvedActorRefs = actorRefs.filter(
      (actorRef) => !portalUserNameCache.has(actorRef),
    )
    if (!unresolvedActorRefs.length) return

    const loadActorNames = async () => {
      const results = await Promise.allSettled(
        unresolvedActorRefs.map(async (actorRef) => {
          const response = await userBXService.show(actorRef)
          const user = response.data.data
          const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
          return { actorRef, name }
        }),
      )

      if (cancelled) return

      const loadedNames: Record<string, string> = {}
      results.forEach((result) => {
        if (result.status !== 'fulfilled' || !result.value.name) return
        portalUserNameCache.set(result.value.actorRef, result.value.name)
        loadedNames[result.value.actorRef] = result.value.name
      })

      if (Object.keys(loadedNames).length) {
        setActorNames((current) => ({ ...current, ...loadedNames }))
      }
    }

    loadActorNames()
    return () => {
      cancelled = true
    }
  }, [portalActorRefsKey])

  const adoptServerSubmission = (report: CashReconciliationDetail) => {
    setSubmission((current) => ({
      ...report,
      stationName: current?.stationName ?? report.stationName,
    }))
  }

  const startDepositEdit = () => {
    if (!submission) return
    setConfirmedDepositInput(minorToMajorInput(submission.confirmedCashDepositMinor))
    setOverrideReasonInput(submission.confirmedCashDepositReason ?? '')
    setDepositError('')
    setIsEditingDeposit(true)
  }

  const cancelDepositEdit = () => {
    if (!submission) return
    setConfirmedDepositInput(minorToMajorInput(submission.confirmedCashDepositMinor))
    setOverrideReasonInput(submission.confirmedCashDepositReason ?? '')
    setDepositError('')
    setIsEditingDeposit(false)
  }

  const saveConfirmedDeposit = async () => {
    if (!submission || !submissionId) return

    const parsedAmount = majorToMinor(confirmedDepositInput)
    if (!parsedAmount.isValid) {
      setDepositError(parsedAmount.error)
      return
    }

    const overrideReason = overrideReasonInput.trim()
    const needsOverrideReason =
      submission.expectedCashDepositMinor !== null &&
      parsedAmount.minor < submission.expectedCashDepositMinor

    if (needsOverrideReason && !overrideReason) {
      setDepositError('Explain why the confirmed deposit is below the expected amount.')
      return
    }
    if (overrideReason.length > 500) {
      setDepositError('Override reason must be 500 characters or fewer.')
      return
    }

    try {
      setMutation('deposit')
      setDepositError('')
      const response = await cashReconciliationBXService.updateConfirmedDeposit(
        submissionId,
        {
          confirmedCashDepositMinor: parsedAmount.minor,
          ...(needsOverrideReason ? { overrideReason } : {}),
        },
      )
      const updatedReport = response.data.data
      adoptServerSubmission(updatedReport)
      setConfirmedDepositInput(minorToMajorInput(updatedReport.confirmedCashDepositMinor))
      setOverrideReasonInput(updatedReport.confirmedCashDepositReason ?? '')
      setIsEditingDeposit(false)
      toast.success('Confirmed deposit saved.')
    } catch (error) {
      setDepositError(getRequestErrorMessage(error, 'Failed to save the confirmed deposit.'))
    } finally {
      setMutation(null)
    }
  }

  const publishReport = async () => {
    if (!submission || !submissionId || submission.confirmedCashDepositMinor === null) return

    try {
      setMutation('publish')
      const response = await cashReconciliationBXService.approve(submissionId)
      adoptServerSubmission(response.data.data)
      setShowPublishModal(false)
      toast.success('Station report published successfully.')
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Failed to publish the station report.'))
    } finally {
      setMutation(null)
    }
  }

  const sendBackReport = async (payload: { notes: string }) => {
    if (!submissionId) return

    try {
      setMutation('reject')
      const response = await cashReconciliationBXService.reject(submissionId, {
        note: payload.notes.trim(),
      })
      const updatedReport = response.data.data
      adoptServerSubmission(updatedReport)
      setConfirmedDepositInput(minorToMajorInput(updatedReport.confirmedCashDepositMinor))
      setOverrideReasonInput(updatedReport.confirmedCashDepositReason ?? '')
      setDepositError('')
      setIsEditingDeposit(false)
      setShowRejectModal(false)
      toast.success('Station report sent back to the manager.')
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Failed to send the station report back.'))
    } finally {
      setMutation(null)
    }
  }

  const retrySettlement = async () => {
    if (!submissionId) return

    try {
      setMutation('retry')
      const response = await cashReconciliationBXService.retrySettlement(submissionId)
      adoptServerSubmission(response.data.data)
      toast.success('Wallet credit retry queued.')
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Failed to retry the wallet credit.'))
    } finally {
      setMutation(null)
    }
  }

  if (loading) {
    return (
      <div className='boostxpress-admin-shell'>
        <div className='bx-cash-recon-detail bx-cash-recon-detail--loading pt-4 mx-3 mx-lg-5'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  if (!submission) {
    return (
      <div className='boostxpress-admin-shell'>
        <div className='bx-cash-recon-detail pt-4 mx-3 mx-lg-5'>
          <div className='alert alert-danger'>{loadError || 'Station report not found.'}</div>
        </div>
      </div>
    )
  }

  const isPending = submission.status === 'ASSOCIATE_APPROVED'
  const uiStatus =
    submission.status === 'ASSOCIATE_APPROVED'
      ? 'pending'
      : submission.status === 'PUBLISHED'
        ? 'approved'
        : 'rejected'
  const statusLabel = submission.status.replace(/_/g, ' ')
  const parsedInput = majorToMinor(confirmedDepositInput)
  const currentDepositMinor =
    isEditingDeposit && parsedInput.isValid
      ? parsedInput.minor
      : submission.confirmedCashDepositMinor
  const requiresOverrideReason =
    currentDepositMinor !== null &&
    submission.expectedCashDepositMinor !== null &&
    currentDepositMinor < submission.expectedCashDepositMinor
  const varianceAmount =
    currentDepositMinor === null || submission.expectedCashDepositMinor === null
      ? null
      : currentDepositMinor - submission.expectedCashDepositMinor
  const varianceStatus =
    varianceAmount === null
      ? null
      : varianceAmount === 0
        ? 'balanced'
        : varianceAmount < 0
          ? 'shortage'
          : 'overage'
  const isMutating = mutation !== null
  const settlement = submission.settlement
  const settlementMessage =
    settlement?.status === 'SETTLED'
      ? 'Paid to the account manager wallet'
      : settlement?.status === 'FAILED'
        ? 'Credit failed'
        : settlement?.failedAttempts
          ? settlement.nextAttemptAt
            ? `Retrying automatically on ${formatDateTime(settlement.nextAttemptAt)}`
            : 'Retrying automatically'
          : 'Credit queued'

  return (
    <div className='boostxpress-admin-shell'>
      <div className='bx-cash-recon-detail pt-4 mx-3 mx-lg-5'>
        <div className='bx-cr-breadcrumb'>
          <button type='button' className='bx-cr-breadcrumb__link' onClick={() => navigate(-1)}>
            &lsaquo; Back to Station Report
          </button>
          <span className='bx-cr-breadcrumb__sep'>&rsaquo;</span>
          <span className='bx-cr-breadcrumb__current'>Report Review</span>
        </div>

        <div className='bx-cr-banner'>
          <div>
            <h1 className='bx-cr-banner__station'>
              {submission.stationName || submission.stationId}
            </h1>
            <div className='bx-cr-banner__meta'>Date: {submission.reportDate}</div>
          </div>
          <div className='bx-cr-banner__badge'>
            <span className={`bx-cr-status bx-cr-status--${uiStatus}`}>{statusLabel}</span>
          </div>
        </div>

        <div className='row m-0'>
          <div className='col-lg-6 mb-4 px-lg-2 px-0'>
            <div className='bx-cr-section'>
              <h3 className='bx-cr-section-title'>Sales by Payment Method</h3>
              <div className='bx-cr-card'>
                <div className='bx-cr-keyval-list'>
                  <div className='bx-cr-keyval-row'>
                    <span className='bx-cr-keyval-label'>POS / Card</span>
                    <span className='bx-cr-keyval-val'>
                      {formatMinorCurrency(submission.posTerminalSalesMinor)}
                    </span>
                  </div>
                  <div className='bx-cr-keyval-divider' />
                  <div className='bx-cr-keyval-row'>
                    <span className='bx-cr-keyval-label'>BoostXpress</span>
                    <span className='bx-cr-keyval-val'>
                      {formatMinorCurrency(submission.walletSalesMinor)}
                    </span>
                  </div>
                  <div className='bx-cr-keyval-divider' />
                  <div className='bx-cr-keyval-row'>
                    <span className='bx-cr-keyval-label'>Bank Transfer</span>
                    <span className='bx-cr-keyval-val'>
                      {formatMinorCurrency(submission.bankTransferSalesMinor)}
                    </span>
                  </div>
                  <div className='bx-cr-keyval-divider' />
                  <div className='bx-cr-keyval-row'>
                    <span className='bx-cr-keyval-label'>Cash</span>
                    <span className='bx-cr-keyval-val'>
                      {formatMinorCurrency(submission.cashSalesMinor)}
                    </span>
                  </div>
                  <div className='bx-cr-keyval-divider' />
                  <div className='bx-cr-keyval-row'>
                    <span className='bx-cr-keyval-label text-dark fw-bold'>Total Sales</span>
                    <span className='bx-cr-keyval-val bx-cr-keyval-val--bold'>
                      {formatMinorCurrency(submission.todaysSalesMinor)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className='bx-cr-section mt-4 mb-0'>
              <h3 className='bx-cr-section-title'>Cash Deposit (As Reported)</h3>
              <div className='bx-cr-card'>
                <div className='bx-cr-keyval-list'>
                  <div className='bx-cr-keyval-row'>
                    <span className='bx-cr-keyval-label'>Cash Collected</span>
                    <span className='bx-cr-keyval-val'>
                      {formatMinorCurrency(submission.closingCashMinor)}
                    </span>
                  </div>
                  <div className='bx-cr-keyval-divider' />
                  <div className='bx-cr-keyval-row'>
                    <span className='bx-cr-keyval-label'>Bank Charges / Expenses</span>
                    <span className='bx-cr-keyval-val bx-cr-keyval-val--danger'>
                      {submission.bankChargesMinor === null
                        ? 'Not provided'
                        : `-${formatMinorCurrency(submission.bankChargesMinor)}`}
                    </span>
                  </div>
                  <div className='bx-cr-keyval-divider' />
                  <div className='bx-cr-keyval-row'>
                    <span className='bx-cr-keyval-label text-dark fw-bold'>
                      Net Cash Deposited (per report)
                    </span>
                    <span className='bx-cr-keyval-val bx-cr-keyval-val--bold'>
                      {formatMinorCurrency(submission.bankDepositsMinor)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='col-lg-6 mb-4 px-lg-2 px-0 d-flex flex-column'>
            <div className='bx-cr-section flex-grow-1 d-flex flex-column mb-0'>
              <h3 className='bx-cr-section-title'>Bank Reconciliation</h3>
              <div className='bx-cr-card bx-cr-card--highlight flex-grow-1 d-flex flex-column'>
                <p className='bx-cr-card__description text-muted mb-4'>
                  Compare what the station reported against what actually landed in the account.
                </p>

                <div className='bx-cr-keyval-list'>
                  <div className='bx-cr-keyval-row'>
                    <span className='bx-cr-keyval-label'>Expected Cash Deposit</span>
                    <span className='bx-cr-keyval-val'>
                      {formatMinorCurrency(submission.expectedCashDepositMinor)}
                    </span>
                  </div>
                  <div className='bx-cr-keyval-divider' />

                  <div className='bx-cr-keyval-row bx-cr-deposit-row'>
                    <span className='bx-cr-keyval-label text-dark fw-medium'>
                      Confirmed Cash Deposit
                    </span>
                    <div className='bx-cr-deposit-control'>
                      {isEditingDeposit ? (
                        <>
                          <div className='bx-cr-input-group'>
                            <span aria-hidden='true'>₦</span>
                            <input
                              type='text'
                              inputMode='decimal'
                              className={`bx-cr-input${depositError ? ' bx-cr-input--error' : ''}`}
                              value={confirmedDepositInput}
                              onChange={(event) => {
                                setConfirmedDepositInput(event.target.value)
                                setDepositError('')
                              }}
                              aria-label='Confirmed cash deposit in naira'
                              aria-describedby={
                                depositError ? 'confirmed-deposit-error' : undefined
                              }
                              disabled={mutation === 'deposit'}
                              autoFocus
                            />
                          </div>
                          <div className='bx-cr-deposit-control__actions'>
                            <button
                              type='button'
                              className='bx-cr-text-button'
                              onClick={cancelDepositEdit}
                              disabled={mutation === 'deposit'}
                            >
                              Cancel
                            </button>
                            <button
                              type='button'
                              className='bx-cr-save-button'
                              onClick={saveConfirmedDeposit}
                              disabled={mutation === 'deposit'}
                            >
                              {mutation === 'deposit' ? 'Saving...' : 'Save deposit'}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <strong>
                            {formatMinorCurrency(submission.confirmedCashDepositMinor)}
                          </strong>
                          {isPending ? (
                            <button
                              type='button'
                              className='bx-cr-text-button bx-cr-text-button--accent'
                              onClick={startDepositEdit}
                              disabled={isMutating}
                            >
                              Edit
                            </button>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                  {isEditingDeposit && requiresOverrideReason ? (
                    <div className='bx-cr-override-field'>
                      <label htmlFor='confirmed-deposit-reason'>
                        Reason for confirming a lower amount
                      </label>
                      <textarea
                        id='confirmed-deposit-reason'
                        value={overrideReasonInput}
                        onChange={(event) => {
                          setOverrideReasonInput(event.target.value)
                          setDepositError('')
                        }}
                        maxLength={500}
                        rows={3}
                        disabled={mutation === 'deposit'}
                        placeholder='Explain the shortage or adjustment.'
                      />
                      <span>{overrideReasonInput.length}/500</span>
                    </div>
                  ) : null}
                  {!isEditingDeposit && submission.confirmedCashDepositReason ? (
                    <div className='bx-cr-saved-reason'>
                      <strong>Override reason:</strong> {submission.confirmedCashDepositReason}
                    </div>
                  ) : null}
                  {depositError ? (
                    <div id='confirmed-deposit-error' className='bx-cr-field-error' role='alert'>
                      {depositError}
                    </div>
                  ) : null}
                  <div className='bx-cr-keyval-divider' />

                  <div className='bx-cr-keyval-row py-2'>
                    <span className='bx-cr-keyval-label'>Variance</span>
                    <span className='bx-cr-keyval-val'>
                      {varianceAmount === null || varianceStatus === null ? (
                        'Not available'
                      ) : (
                        <span className={`bx-cr-badge bx-cr-badge--${varianceStatus}`}>
                          {formatMinorCurrency(Math.abs(varianceAmount))} &nbsp;&bull;&nbsp;{' '}
                          {varianceStatus === 'balanced'
                            ? 'Balanced'
                            : varianceStatus === 'shortage'
                              ? 'Shortage'
                              : 'Overage'}
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                {isPending ? (
                  <div className='bx-cr-actions mt-auto'>
                    <button
                      type='button'
                      className='bx-btn-reject'
                      onClick={() => setShowRejectModal(true)}
                      disabled={isMutating}
                    >
                      Send back for correction
                    </button>
                    <button
                      type='button'
                      className='bx-btn-approve'
                      onClick={() => setShowPublishModal(true)}
                      disabled={
                        isMutating ||
                        isEditingDeposit ||
                        submission.confirmedCashDepositMinor === null
                      }
                    >
                      {mutation === 'publish' ? 'Publishing...' : 'Publish report'}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {settlement ? (
          <section className='bx-cr-section bx-cr-settlement-section'>
            <h3 className='bx-cr-section-title'>Wallet Credit</h3>
            <div className={`bx-cr-card bx-cr-settlement bx-cr-settlement--${settlement.status.toLowerCase()}`}>
              <div className='bx-cr-settlement__header'>
                <div>
                  <span className='bx-cr-settlement__status'>{settlement.status}</span>
                  <p>{settlementMessage}</p>
                </div>
                {settlement.status === 'FAILED' ? (
                  <button
                    type='button'
                    className='bx-cr-retry-button'
                    onClick={retrySettlement}
                    disabled={isMutating}
                  >
                    {mutation === 'retry' ? 'Retrying...' : 'Retry wallet credit'}
                  </button>
                ) : null}
              </div>
              <div className='bx-cr-settlement__details'>
                <div><span>Amount</span><strong>{formatMinorCurrency(settlement.amountMinor)}</strong></div>
                <div><span>Transaction ID</span><strong>{settlement.tradegridTransactionId || 'Not available'}</strong></div>
                <div><span>Failed attempts</span><strong>{settlement.failedAttempts}</strong></div>
                <div><span>Last updated</span><strong>{formatDateTime(settlement.updatedAt)}</strong></div>
              </div>
              {settlement.lastError ? <p className='bx-cr-settlement__error'>{settlement.lastError}</p> : null}
            </div>
          </section>
        ) : null}

        {submission.statusHistory.length ? (
          <section className='bx-cr-section bx-cr-history-section'>
            <h3 className='bx-cr-section-title'>Report History</h3>
            <div className='bx-cr-card bx-cr-history'>
              {submission.statusHistory.map((entry, index) => (
                <div className='bx-cr-history__item' key={`${entry.status}-${entry.at}-${index}`}>
                  <span className='bx-cr-history__dot' aria-hidden='true' />
                  <div>
                    <strong>{entry.status.replace(/_/g, ' ')}</strong>
                    <p>
                      {formatDateTime(entry.at)} &middot;{' '}
                      {entry.actorRefKind === 'adminEmail' ? (
                        <>Finance: {entry.actorRef}</>
                      ) : (
                        <>Portal user: {actorNames[entry.actorRef] || entry.actorRef}</>
                      )}
                    </p>
                    {entry.note ? <p className='bx-cr-history__note'>{entry.note}</p> : null}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <PublishReconciliationModal
          show={showPublishModal}
          onHide={() => setShowPublishModal(false)}
          onConfirm={publishReport}
          submission={submission}
          submitting={mutation === 'publish'}
        />

        <RejectReconciliationModal
          show={showRejectModal}
          onHide={() => setShowRejectModal(false)}
          onConfirm={sendBackReport}
          submission={submission}
          submitting={mutation === 'reject'}
        />
      </div>
    </div>
  )
}

export default CashReconciliationDetailPage
